import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Badge, Row, Col, Statistic, Typography, Popconfirm, Alert,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  PlusOutlined, DeleteOutlined, StopOutlined,
  BellOutlined, WarningOutlined, InfoCircleOutlined,
  ExclamationCircleOutlined, ClockCircleOutlined, DollarOutlined,
  ToolOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useCondoStore } from '../store/condoStore';
import { fromNowServer } from '../utils/relativeTime';
import type { OwnerNotification, NotificationCategory } from '../types';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_CONFIG: Record<NotificationCategory, { color: string; icon: React.ReactNode; label: string }> = {
  INFO:        { color: 'blue',    icon: <InfoCircleOutlined />,        label: 'Info' },
  WARNING:     { color: 'orange',  icon: <WarningOutlined />,           label: 'Warning' },
  URGENT:      { color: 'red',     icon: <ExclamationCircleOutlined />, label: 'Urgent' },
  REMINDER:    { color: 'purple',  icon: <ClockCircleOutlined />,       label: 'Reminder' },
  MAINTENANCE: { color: 'cyan',    icon: <ToolOutlined />,              label: 'Maintenance' },
  FINANCIAL:   { color: 'green',   icon: <DollarOutlined />,            label: 'Financial' },
};

const NotificationCenter: React.FC = () => {
  const {
    notifications, fetchNotifications, createNotification,
    updateNotification, deactivateNotification, deleteNotification,
    currentCondo, isLoading,
  } = useCondoStore() as any;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState<OwnerNotification | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchNotifications(currentCondo.id, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchNotifications is a stable zustand action
  }, [currentCondo?.id]);

  const filtered = notifications.filter((n: OwnerNotification) =>
    filterCategory === 'all' || n.category === filterCategory
  );

  const stats = {
    total: notifications.length,
    active: notifications.filter((n: OwnerNotification) => n.active).length,
    urgent: notifications.filter((n: OwnerNotification) => n.category === 'URGENT' && n.active).length,
  };

  const handleOpenCreate = () => {
    setEditingNotif(null);
    form.resetFields();
    form.setFieldsValue({ category: 'INFO', targetType: 'ALL' });
    setModalOpen(true);
  };

  const handleOpenEdit = (n: OwnerNotification) => {
    setEditingNotif(n);
    form.setFieldsValue({
      title: n.title,
      message: n.message,
      category: n.category,
      targetType: n.targetType,
      targetUnit: n.targetUnit,
      createdBy: n.createdBy,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, condoId: currentCondo?.id };
      if (editingNotif) {
        await updateNotification(editingNotif.id, payload);
        staticApi.message.success('Notification updated');
      } else {
        await createNotification(payload);
        staticApi.message.success('Notification sent');
      }
      setModalOpen(false);
    } catch { /* validation */ }
  };

  const handleDeactivate = async (id: string) => {
    await deactivateNotification(id);
    staticApi.message.info('Notification deactivated');
  };

  const handleDelete = async (id: string) => {
    await deleteNotification(id);
    staticApi.message.success('Notification deleted');
  };

  const columns: ColumnsType<OwnerNotification> = [
    {
      title: 'Notification',
      key: 'info',
      render: (_, r) => {
        const cfg = CATEGORY_CONFIG[r.category] || CATEGORY_CONFIG.INFO;
        return (
          <Space direction="vertical" size={2}>
            <Space>
              {cfg.icon}
              <Text strong>{r.title}</Text>
              {!r.active && <Tag color="default">Inactive</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.message.slice(0, 80)}{r.message.length > 80 ? '…' : ''}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat: NotificationCategory) => {
        const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.INFO;
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Target',
      key: 'target',
      width: 140,
      render: (_, r) => {
        if (r.targetType === 'ALL') return <Tag color="geekblue">All Owners</Tag>;
        if (r.targetType === 'UNIT') return <Tag color="cyan">Unit {r.targetUnit}</Tag>;
        return <Tag color="purple">Specific Owner</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active: boolean) => (
        <Badge status={active ? 'success' : 'default'} text={active ? 'Active' : 'Inactive'} />
      ),
    },
    {
      title: 'Sent',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (d: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{fromNowServer(d)}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => handleOpenEdit(r)}>Edit</Button>
          {r.active && (
            <Popconfirm title="Deactivate this notice?" onConfirm={() => handleDeactivate(r.id)} okText="Yes">
              <Button size="small" icon={<StopOutlined />} title="Deactivate" />
            </Popconfirm>
          )}
          <Popconfirm title="Delete permanently?" onConfirm={() => handleDelete(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <BellOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            Notification Center
          </Title>
          <Text type="secondary">Send notices, reminders, and alerts to owners and residents</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Send Notification
        </Button>
      </div>

      {/* KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={8}>
          <Card>
            <Statistic title="Total Sent" value={stats.total} prefix={<BellOutlined />} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card>
            <Statistic title="Currently Active" value={stats.active} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card>
            <Statistic title="Urgent Notices" value={stats.urgent} valueStyle={{ color: stats.urgent > 0 ? '#ff4d4f' : undefined }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      {stats.urgent > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${stats.urgent} urgent notice${stats.urgent > 1 ? 's' : ''} currently active`}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filter */}
      <Space style={{ marginBottom: 16 }}>
        <Select value={filterCategory} onChange={setFilterCategory} style={{ width: 180 }}>
          <Option value="all">All Categories</Option>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
            <Option key={k} value={k}>{v.label}</Option>
          ))}
        </Select>
      </Space>

      {/* Table */}
      <Card>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 15 }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: '8px 0' }}>
                <Text type="secondary">Full message: </Text>
                <Text>{r.message}</Text>
                {r.createdBy && (
                  <div><Text type="secondary" style={{ fontSize: 12 }}>Sent by: {r.createdBy}</Text></div>
                )}
              </div>
            ),
          }}
          rowClassName={(r) => r.active ? '' : 'opacity-50'}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingNotif ? 'Edit Notification' : 'Send New Notification'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingNotif ? 'Save Changes' : 'Send'}
        width={560}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. Water Shutoff Notice — Unit 4xx" />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}>
            <TextArea rows={4} placeholder="Full notification message…" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                    <Option key={k} value={k}>{v.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="targetType" label="Send To" rules={[{ required: true }]}>
                <Select>
                  <Option value="ALL">All Owners</Option>
                  <Option value="UNIT">Specific Unit</Option>
                  <Option value="OWNER">Specific Owner</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.targetType !== cur.targetType}>
            {({ getFieldValue }) =>
              getFieldValue('targetType') === 'UNIT' ? (
                <Form.Item name="targetUnit" label="Unit Number" rules={[{ required: true }]}>
                  <Input placeholder="e.g. 401" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="createdBy" label="Sent By (optional)">
            <Input placeholder="e.g. Building Manager" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationCenter;
